import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus, Trash2, Phone, Car, Save } from "lucide-react";

const WEEKDAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const MONTHS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

function pad(n) { return String(n).padStart(2, "0"); }
function toISODate(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function isSameISO(a, b) { return a === b; }

export default function Agenda() {
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogState, setDialogState] = useState(null); // { mode: "create"|"edit", date, apt? }

  const year = cursor.getFullYear();
  const month = cursor.getMonth() + 1;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/appointments", { params: { year, month } });
      setAppointments(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar agenda");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [year, month]); // eslint-disable-line react-hooks/exhaustive-deps

  const grid = useMemo(() => {
    // Constrói 6 semanas x 7 dias começando no domingo
    const firstDay = new Date(year, month - 1, 1);
    const startOffset = firstDay.getDay(); // 0=domingo
    const gridStart = new Date(year, month - 1, 1 - startOffset);
    const cells = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      cells.push(d);
    }
    return cells;
  }, [year, month]);

  const aptsByDate = useMemo(() => {
    const map = {};
    for (const a of appointments) {
      if (!map[a.date]) map[a.date] = [];
      map[a.date].push(a);
    }
    return map;
  }, [appointments]);

  const goPrev = () => setCursor(new Date(year, month - 2, 1));
  const goNext = () => setCursor(new Date(year, month, 1));
  const goToday = () => setCursor(new Date(today.getFullYear(), today.getMonth(), 1));

  const todayISO = toISODate(today);

  return (
    <div className="space-y-6" data-testid="agenda-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
            <CalendarIcon className="w-3.5 h-3.5" /> Agenda
          </div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mt-1" data-testid="agenda-title">
            {MONTHS[month - 1]} <span className="text-muted-foreground font-normal">{year}</span>
          </h1>
        </div>
        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="sm" onClick={goPrev} data-testid="agenda-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToday} data-testid="agenda-today">Hoje</Button>
          <Button variant="outline" size="sm" onClick={goNext} data-testid="agenda-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="border border-border rounded-md overflow-hidden bg-card">
        <div className="grid grid-cols-7 bg-secondary/60 text-[10px] uppercase tracking-widest text-muted-foreground">
          {WEEKDAYS.map((w) => (
            <div key={w} className="px-2 py-2 text-center border-r border-border last:border-r-0">{w}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 auto-rows-fr">
          {grid.map((d, i) => {
            const iso = toISODate(d);
            const inMonth = d.getMonth() === month - 1;
            const isToday = isSameISO(iso, todayISO);
            const items = aptsByDate[iso] || [];
            return (
              <button
                key={iso + i}
                type="button"
                onClick={() => setDialogState({ mode: "create", date: iso })}
                className={[
                  "text-left border-r border-b border-border last:border-r-0 min-h-[110px] p-1.5 relative group transition-colors",
                  inMonth ? "bg-card hover:bg-secondary/40" : "bg-background/40 text-muted-foreground/60",
                  isToday ? "ring-1 ring-primary/60 ring-inset z-10" : "",
                ].join(" ")}
                data-testid={`agenda-day-${iso}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs font-semibold ${isToday ? "text-primary" : ""}`}>{d.getDate()}</span>
                  {inMonth && (
                    <span className="opacity-0 group-hover:opacity-100 text-muted-foreground transition-opacity">
                      <Plus className="w-3 h-3" />
                    </span>
                  )}
                </div>
                <div className="space-y-1">
                  {items.slice(0, 3).map((a) => (
                    <div
                      key={a.id}
                      onClick={(e) => { e.stopPropagation(); setDialogState({ mode: "edit", date: iso, apt: a }); }}
                      className="text-[11px] px-1.5 py-0.5 rounded bg-primary/10 border border-primary/30 text-foreground truncate hover:bg-primary/20"
                      data-testid={`agenda-spot-${a.id}`}
                      title={`${a.client_name} — ${a.reason || ""}`}
                    >
                      <span className="font-medium">{a.client_name}</span>
                      {a.reason && <span className="text-muted-foreground"> · {a.reason}</span>}
                    </div>
                  ))}
                  {items.length > 3 && (
                    <div className="text-[10px] text-muted-foreground">+{items.length - 3} agendamentos</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {loading && <div className="text-xs text-muted-foreground">Carregando agenda…</div>}

      <AppointmentDialog
        state={dialogState}
        onOpenChange={(v) => !v && setDialogState(null)}
        onSaved={load}
      />
    </div>
  );
}

function AppointmentDialog({ state, onOpenChange, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!state) { setForm(null); return; }
    if (state.mode === "edit" && state.apt) {
      setForm({
        date: state.apt.date,
        client_name: state.apt.client_name || "",
        phone: state.apt.phone || "",
        vehicle: state.apt.vehicle || "",
        reason: state.apt.reason || "",
      });
    } else {
      setForm({ date: state.date, client_name: "", phone: "", vehicle: "", reason: "" });
    }
  }, [state]);

  if (!state || !form) return null;
  const isEdit = state.mode === "edit";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_name.trim()) return toast.error("Informe o nome do cliente");
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/appointments/${state.apt.id}`, form);
        toast.success("Agendamento atualizado");
      } else {
        await api.post("/appointments", form);
        toast.success("Agendamento criado");
      }
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  };

  const del = async () => {
    if (!isEdit) return;
    if (!window.confirm("Remover este agendamento?")) return;
    setDeleting(true);
    try {
      await api.delete(`/appointments/${state.apt.id}`);
      toast.success("Agendamento removido");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao remover");
    } finally { setDeleting(false); }
  };

  const dateLabel = (() => {
    try {
      const [y, m, d] = form.date.split("-").map(Number);
      const dt = new Date(y, m - 1, d);
      return `${dt.getDate()} de ${MONTHS[dt.getMonth()]} de ${dt.getFullYear()}`;
    } catch { return form.date; }
  })();

  return (
    <Dialog open={!!state} onOpenChange={onOpenChange}>
      <DialogContent data-testid="appointment-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar agendamento" : "Novo agendamento"}</DialogTitle>
          <DialogDescription>{dateLabel}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Nome do cliente</Label>
            <Input
              value={form.client_name}
              onChange={(e) => setForm({ ...form, client_name: e.target.value })}
              autoFocus
              placeholder="Ex: João da Silva"
              data-testid="apt-client-name"
            />
          </div>
          <div>
            <Label className="flex items-center gap-1.5"><Phone className="w-3 h-3" /> Telefone</Label>
            <Input
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="(00) 00000-0000"
              data-testid="apt-phone"
            />
          </div>
          <div>
            <Label className="flex items-center gap-1.5"><Car className="w-3 h-3" /> Veículo</Label>
            <Input
              value={form.vehicle}
              onChange={(e) => setForm({ ...form, vehicle: e.target.value })}
              placeholder="Ex: Honda Civic 2020 · ABC-1234"
              data-testid="apt-vehicle"
            />
          </div>
          <div>
            <Label>Causa da visita</Label>
            <Textarea
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="Ex: Troca de óleo, revisão dos freios, inspeção geral..."
              rows={3}
              data-testid="apt-reason"
            />
          </div>
          <DialogFooter className="flex-row justify-between sm:justify-between gap-2">
            <div>
              {isEdit && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={del}
                  disabled={deleting}
                  className="text-muted-foreground hover:text-red-500"
                  data-testid="apt-delete"
                >
                  <Trash2 className="w-4 h-4 mr-1.5" /> {deleting ? "Removendo…" : "Remover"}
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid="apt-save">
                <Save className="w-4 h-4 mr-1.5" /> {saving ? "Salvando…" : "Salvar"}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
